import React, { useState } from "react";
import { NetworkMemberType } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import { Button } from "components/Graphs/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import { RadioGroup, RadioGroupItem } from "components/Graphs/RadioGroup";
import {
  networkModeLabel,
  networkModes,
} from "components/Environment/Networks/networkMode";
import { toast } from "utils/toast";

interface EditNetworkModeDialogProps {
  name: string;
  memberType: NetworkMemberType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSetMode: (mode: NetworkMemberType) => Promise<void>;
}

const EditNetworkModeForm: React.FC<
  Omit<EditNetworkModeDialogProps, "open">
> = ({ name, memberType, onOpenChange, onSetMode }) => {
  const [selectedMode, setSelectedMode] = useState<NetworkMemberType | null>(
    memberType,
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (saving || selectedMode === null || selectedMode === memberType) return;
    setSaving(true);
    try {
      await onSetMode(selectedMode);
      toast.success(
        `Network mode changed to ${networkModeLabel(selectedMode)}.`,
      );
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update network mode", error);
      toast.error(`Could not update the network mode for ${name}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit Network Mode</DialogTitle>
        <DialogDescription>
          Choose the network mode for {name} within this organization&apos;s
          private mesh network.
        </DialogDescription>
      </DialogHeader>
      <RadioGroup
        aria-label="Network mode"
        value={selectedMode === null ? undefined : String(selectedMode)}
        onValueChange={(value) =>
          setSelectedMode(Number(value) as NetworkMemberType)
        }
        className="gap-3"
      >
        {networkModes.map(({ type, label, description }) => (
          <label
            key={type}
            className="flex cursor-pointer items-start gap-3 rounded-md border border-borderPrimary bg-secondaryBg p-4 text-primary has-[[data-state=checked]]:border-highlight"
          >
            <RadioGroupItem
              value={String(type)}
              disabled={saving}
              className="mt-0.5 shrink-0"
            />
            <span className="min-w-0">
              <span className="flex items-center gap-2 text-sm font-medium">
                {label}
                {type === memberType && (
                  <span className="rounded bg-highlight/20 px-2 py-0.5 text-xs font-semibold text-highlight">
                    Current
                  </span>
                )}
              </span>
              <span className="mt-1 block text-xs text-secondary">
                {description}
              </span>
            </span>
          </label>
        ))}
      </RadioGroup>
      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          disabled={saving}
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          className="disabled:bg-highlight disabled:text-black"
          disabled={
            saving || selectedMode === null || selectedMode === memberType
          }
          isLoading={saving}
          loadingText="Saving..."
          onClick={() => void save()}
        >
          Save
        </Button>
      </DialogFooter>
    </>
  );
};

export const EditNetworkModeDialog: React.FC<EditNetworkModeDialogProps> = ({
  open,
  onOpenChange,
  ...props
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg">
      {open && <EditNetworkModeForm onOpenChange={onOpenChange} {...props} />}
    </DialogContent>
  </Dialog>
);
